import { BrandColors, Colors } from "@/constants/theme";
import Ionicons from "@react-native-vector-icons/ionicons";
import React from "react";
import {
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
  useColorScheme,
} from "react-native";
import { ThemedText } from "../themed-text";
import { Label } from "./label";

interface CounterInputProps {
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  label?: string;
  style?: ViewStyle;
}

export function CounterInput({
  value,
  onValueChange,
  min = 0,
  max = 50,
  label,
  style,
}: CounterInputProps) {
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];

  const handleIncrement = () => {
    if (value < max) {
      onValueChange(value + 1);
    }
  };

  const handleDecrement = () => {
    if (value > min) {
      onValueChange(value - 1);
    }
  };

  return (
    <View style={[styles.container, style]}>
      {label ? <Label label={label} /> : null}
      <View
        style={[
          styles.counterContainer,
          { backgroundColor: theme.background, borderColor: theme.border },
        ]}
      >
        <TouchableOpacity
          style={[
            styles.button,
            { borderColor: theme.border },
            value <= min && styles.buttonDisabled,
          ]}
          onPress={handleDecrement}
          disabled={value <= min}
        >
          <Ionicons
            name="remove"
            size={20}
            color={
              value <= min ? BrandColors.lightGray : BrandColors.mediumGray
            }
          />
        </TouchableOpacity>

        <ThemedText style={styles.value}>{value}</ThemedText>

        <TouchableOpacity
          style={[
            styles.button,
            { borderColor: theme.border },
            value >= max && styles.buttonDisabled,
          ]}
          onPress={handleIncrement}
          disabled={value >= max}
        >
          <Ionicons
            name="add"
            size={20}
            color={
              value >= max ? BrandColors.lightGray : BrandColors.mediumGray
            }
          />
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 12,
  },
  counterContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 14,
    borderWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
    minHeight: 64,
  },
  button: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: BrandColors.white,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  value: {
    fontSize: 24,
    minWidth: 40,
    textAlign: "center",
  },
});
